import React, { useState } from "react";
import { Gift, Droplet, Wine, Wheat, Sparkles, Cherry, FlaskConical, Leaf, Salad, BookOpen } from "lucide-react";
import { useLang } from "../lib/i18n";
import CategoryDialog from "./CategoryDialog";
import RecipeBook from "./RecipeBook";

const CATEGORIES = [
  { key: "giftbox", icon: Gift,         tag: "GIFT BOX",     image: "/cats/giftbox.jpg",  featured: true },
  { key: "recipes", icon: BookOpen,     tag: "ΧΩΡΙΑ ΚΡΗΤΗΣ",  image: "/cats/recipes.jpg", customDialog: "recipes" },
  { key: "oil",     icon: Droplet,      tag: "EXTRA VIRGIN", image: "/cats/oil.jpg" },
  { key: "olives",  icon: Cherry,       tag: "ΚΑΛΑΜΩΝ",      image: "/cats/olives.jpg" },
  { key: "honey",   icon: Sparkles,     tag: "ΘΥΜΑΡΙΣΙΟ",    image: "/cats/honey.jpg" },
  { key: "raki",    icon: FlaskConical, tag: "ΚΡΗΤΗΣ",       image: "/cats/raki.jpg" },
  { key: "wine",    icon: Wine,         tag: "ΑΣΥΡΤΙΚΟ",     image: "/cats/wine.jpg" },
  { key: "vinegar", icon: Salad,        tag: "BALSAMIC",     image: "/cats/vinegar.jpg" },
  { key: "rusks",   icon: Wheat,        tag: "ΝΤΑΚΟΣ",       image: "/cats/rusks.jpg" },
  { key: "herbs",   icon: Leaf,         tag: "ΡΙΓΑΝΗ",       image: "/cats/herbs.jpg" },
];

export const CretanHeritage = () => {
  const { t } = useLang();
  const [openKey, setOpenKey] = useState(null);
  const [recipeOpen, setRecipeOpen] = useState(false);

  const openCategory = (cat) => {
    if (cat.customDialog === "recipes") {
      setRecipeOpen(true);
    } else {
      setOpenKey(cat.key);
    }
  };

  return (
    <section
      id="heritage"
      data-testid="heritage-section"
      className="relative py-32 sm:py-48 px-6 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 18%, rgba(0,0,0,0.55) 82%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="mb-12 text-center">
          <p
            data-testid="heritage-eyebrow"
            className="font-display text-[10px] tracking-[0.55em] text-[color:var(--goldenelia-gold)] uppercase mb-6"
          >
            {t("cretan.eyebrow")}
          </p>
          <h2
            data-testid="heritage-title"
            className="font-serif-luxury text-4xl sm:text-5xl lg:text-6xl gold-gradient-text mb-8 leading-[1.1]"
          >
            {t("cretan.title").split("\n").map((l, i) => (
              <span key={i} className="block italic font-light">
                {l}
              </span>
            ))}
          </h2>
          <div className="gold-divider max-w-xs mx-auto">
            <span className="diamond" />
          </div>
          <p className="font-serif-luxury italic text-lg text-[color:var(--goldenelia-cream)]/75 mt-8 max-w-2xl mx-auto leading-relaxed">
            {t("cretan.sub")}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const name = t(`cretan.cats.${c.key}.name`);
            const desc = t(`cretan.cats.${c.key}.desc`);
            return (
              <button
                type="button"
                key={c.key}
                data-testid={`heritage-card-${c.key}`}
                onClick={() => openCategory(c)}
                className={`group relative overflow-hidden text-left cursor-pointer border border-[color:var(--goldenelia-gold)]/15 hover:border-[color:var(--goldenelia-gold)]/55 transition-all duration-500 ${
                  c.featured ? "col-span-2 lg:col-span-1 row-span-1" : ""
                }`}
                style={{ minHeight: c.featured ? "340px" : "300px" }}
              >
                {/* Background image */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                  style={{
                    backgroundImage: `url('${c.image}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                {/* Dark gradient for readability */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.92) 100%)",
                  }}
                />
                {/* Gold corner accents */}
                <span className="pointer-events-none absolute top-3 left-3 w-7 h-px bg-[color:var(--goldenelia-gold)]/70 z-10" />
                <span className="pointer-events-none absolute top-3 left-3 w-px h-7 bg-[color:var(--goldenelia-gold)]/70 z-10" />
                <span className="pointer-events-none absolute bottom-3 right-3 w-7 h-px bg-[color:var(--goldenelia-gold)]/70 z-10" />
                <span className="pointer-events-none absolute bottom-3 right-3 w-px h-7 bg-[color:var(--goldenelia-gold)]/70 z-10" />

                {/* Content */}
                <div className="relative z-10 h-full p-6 sm:p-7 flex flex-col">
                  <div className="flex items-start justify-between mb-auto">
                    <Icon
                      size={26}
                      strokeWidth={1.2}
                      className="text-[color:var(--goldenelia-gold)] transition-transform duration-700 group-hover:scale-110"
                    />
                    <span className="font-display text-[8px] tracking-[0.3em] text-[color:var(--goldenelia-gold)]/85 uppercase pt-1">
                      {c.tag}
                    </span>
                  </div>

                  <div className="mt-auto">
                    <h3 className="font-serif-luxury text-2xl sm:text-3xl text-[color:var(--goldenelia-cream)] mb-2 leading-tight">
                      {name}
                    </h3>
                    <p className="font-body text-[12px] text-[color:var(--goldenelia-cream)]/65 leading-relaxed mb-5 line-clamp-2">
                      {desc}
                    </p>
                    <span className="inline-block text-[10px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-gold)] group-hover:text-[color:var(--goldenelia-cream)] transition-colors duration-300 border-b border-[color:var(--goldenelia-gold)]/40 group-hover:border-[color:var(--goldenelia-cream)] pb-1 font-display">
                      {t("cretan.view")} →
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <p
          data-testid="heritage-note"
          className="text-center font-body text-xs text-[color:var(--goldenelia-cream)]/45 mt-12 tracking-wider max-w-2xl mx-auto leading-relaxed"
        >
          {t("cretan.note")}
        </p>
      </div>

      <CategoryDialog
        open={!!openKey}
        onOpenChange={(v) => !v && setOpenKey(null)}
        categoryKey={openKey}
      />

      <RecipeBook open={recipeOpen} onClose={() => setRecipeOpen(false)} />
    </section>
  );
};

export default CretanHeritage;
